import { useCallback, useEffect, useRef, useState } from "react";
import {
  getMyProductEntitlement,
  isBreakageMockMode,
} from "services/breakage-service";
import {
  buildBreakageSessionKey,
  createRequestGuard,
  isValidBreakageSession,
  reconcileEntitlementWithSession,
} from "views/breakage/breakage-request-guard";

const initialState = (sessionKey = null) => ({
  loading: true,
  entitlement: null,
  kind: null,
  enabled: false,
  unauthorized: false,
  forbidden: false,
  unavailable: false,
  scopeMismatch: false,
  error: null,
  sessionKey,
});

const invalidSessionState = (sessionKey) => ({
  ...initialState(sessionKey),
  loading: false,
  kind: "unauthorized",
  unauthorized: true,
  error: "Sessão sem empresa ou identidade válida para consultar Breakage.",
});

/**
 * @param {string|number|null|undefined} companyId
 * @param {{ identityKey?: string|number|null }} [options]
 */
export const useBreakageEntitlement = (companyId, options = {}) => {
  const identityKey = options.identityKey ?? null;
  const sessionKey = buildBreakageSessionKey(companyId, identityKey);
  const sessionValid = isValidBreakageSession(companyId, identityKey);

  const guardRef = useRef(createRequestGuard());
  const sessionKeyRef = useRef(sessionKey);
  const companyIdRef = useRef(companyId);
  const identityKeyRef = useRef(identityKey);
  sessionKeyRef.current = sessionKey;
  companyIdRef.current = companyId;
  identityKeyRef.current = identityKey;

  const [state, setState] = useState(() =>
    sessionValid ? initialState(sessionKey) : invalidSessionState(sessionKey)
  );

  const reload = useCallback(async () => {
    const requestSessionKey = sessionKeyRef.current;
    const requestCompanyId = companyIdRef.current;
    const requestIdentityKey = identityKeyRef.current;

    if (!isValidBreakageSession(requestCompanyId, requestIdentityKey)) {
      guardRef.current.invalidate();
      setState(invalidSessionState(requestSessionKey));
      return;
    }

    const requestToken = guardRef.current.begin();
    setState({
      ...initialState(requestSessionKey),
      loading: true,
    });

    try {
      const result = await getMyProductEntitlement();
      if (!guardRef.current.isCurrent(requestToken)) {
        return;
      }
      if (sessionKeyRef.current !== requestSessionKey) {
        return;
      }

      setState(
        reconcileEntitlementWithSession(result, {
          expectedCompanyId: requestCompanyId,
          sessionKey: requestSessionKey,
          requireOwnerMatch: !isBreakageMockMode(),
        })
      );
    } catch (ex) {
      if (!guardRef.current.isCurrent(requestToken)) {
        return;
      }
      if (sessionKeyRef.current !== requestSessionKey) {
        return;
      }
      setState({
        ...initialState(requestSessionKey),
        loading: false,
        kind: "unavailable",
        unavailable: true,
        error: ex?.message || "Falha ao consultar entitlement",
      });
    }
  }, []);

  useEffect(() => {
    const guard = guardRef.current;
    guard.invalidate();

    if (!sessionValid) {
      setState(invalidSessionState(sessionKey));
      return () => {
        guard.invalidate();
      };
    }

    setState(initialState(sessionKey));
    reload();

    return () => {
      guard.invalidate();
    };
  }, [sessionKey, sessionValid, reload]);

  // Sessão inválida (logout / sem empresa): derivar unauthorized no render,
  // sem esperar useEffect — evita flash de "Breakage não contratado".
  if (!sessionValid) {
    return {
      ...invalidSessionState(sessionKey),
      reload,
    };
  }

  const belongsToCurrentSession =
    state.sessionKey === sessionKey && !state.loading;

  // Enquanto a chave mudou e o estado ainda não acompanhou, permanece loading
  const loading =
    state.loading || state.sessionKey !== sessionKey;

  const enabledForCurrentSession =
    belongsToCurrentSession && state.enabled && !state.scopeMismatch;

  return {
    ...state,
    loading,
    enabled: enabledForCurrentSession,
    unauthorized: belongsToCurrentSession && state.unauthorized,
    forbidden: belongsToCurrentSession && state.forbidden,
    unavailable: belongsToCurrentSession && state.unavailable,
    scopeMismatch: belongsToCurrentSession && state.scopeMismatch,
    reload,
  };
};
